import { weenatIntl as wintl } from '@weenat/wintl';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import { isHydricStress, isMetric } from '../../enums/MetricIds';
const backEndAggregates = [
    'minToday',
    'maxToday',
    'latest',
    'lastSlidingHour',
    'lastTwelveHours',
    'today',
    'yesterday',
    'week'
];
export const aggregates = [...backEndAggregates, 'untilMidnight'];
export const isBackEndAggregationId = (v) => {
    return backEndAggregates.includes(v);
};
export const isAggregationId = (v) => {
    return aggregates.includes(v);
};
export const getAggregationLabel = (summaryAggregateId) => {
    let label;
    const capitalizedT = (key) => 
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    wintl.t(key, { capitalize: true });
    const aggregationsLabels = {
        minToday: `${capitalizedT('charts.min')} ${wintl.t('periods.today_abbr')}`,
        maxToday: `${capitalizedT('charts.max')} ${wintl.t('periods.today_abbr')}`,
        latest: capitalizedT('periods.latest'),
        lastSlidingHour: capitalizedT('periods.last_sliding_hour'),
        today: capitalizedT('periods.since_midnight_abbr'),
        untilMidnight: capitalizedT('periods.until_midnight_abbr'),
        yesterday: capitalizedT('periods.last_day'),
        lastTwelveHours: capitalizedT('periods.last_12_hours'),
        week: capitalizedT('periods.last_7_days_abbr')
    };
    if (isMetric(summaryAggregateId)) {
        label =
            capitalizedT(`metrics.${summaryAggregateId}_abbr`) ||
                capitalizedT(`metrics.${summaryAggregateId}`);
    }
    else {
        label = aggregationsLabels[summaryAggregateId];
    }
    return label;
};
const keyByAggregationId = {
    lastSlidingHour: 'H',
    lastTwelveHours: '12H',
    latest: 'L',
    maxToday: 'X',
    minToday: 'N',
    today: 'D',
    yesterday: 'DD',
    week: '7D'
};
const aggregatesWithTimestamps = ['minToday', 'maxToday', 'latest'];
const hydricStressAggregates = ['7D', 'DD', 'D', 'L'];
const isHydricStressAggregates = (key) => {
    return hydricStressAggregates.includes(key);
};
const hydricDeficitAggregates = ['3D', 'DD', 'D'];
const isHydricDeficitAggregates = (key) => {
    return hydricDeficitAggregates.includes(key);
};
/**
 * Gets the value and timestamp from of the given aggregate of the given metric from the given summary
 */
export const getAggregateValue = (metric, aggregate, summary, horizonId) => {
    let result = {
        value: null
    };
    if (!isNil(summary)) {
        const key = keyByAggregationId[aggregate];
        if (isHydricStress(metric)) {
            const isCapa = metric === 'U_CAPA';
            const nonNullHorizonId = isNil(horizonId) ? -1 : horizonId;
            if (isHydricStressAggregates(key)) {
                if (isCapa) {
                    const aggregationKey = `${metric}_${key}`;
                    const tuple = summary[aggregationKey];
                    const ts = summary[`${metric}_Li`];
                    result = {
                        value: !isNil(tuple) ? tuple[0] : null,
                        timestamp: !isNil(ts) ? ts : undefined
                    };
                }
                else {
                    const aggregationKey = `${metric}_${key}`;
                    const tuple = summary[aggregationKey]?.[nonNullHorizonId];
                    const tsByHrz = summary[`${metric}_Li`];
                    const ts = !isNil(tsByHrz) ? tsByHrz[nonNullHorizonId] : undefined;
                    return (result = {
                        value: !isNil(tuple) ? tuple[0] : null,
                        timestamp: !isNil(ts) ? ts : undefined
                    });
                }
            }
            else {
                throw new Error(`${aggregate} is not an hydric stress aggregate`);
            }
        }
        else if (metric === 'HDEF') {
            if (isHydricDeficitAggregates(key)) {
                const aggregationKey = `${metric}_${key}`;
                const tuple = summary[aggregationKey];
                const ts = summary[`${aggregationKey}i`] ?? undefined;
                result = {
                    value: !isNil(tuple) ? tuple[0] : null,
                    timestamp: ts
                };
            }
            else {
                throw new Error(`${aggregate} is not an hydric stress aggregate`);
            }
        }
        else {
            const summaryKey = `${metric}_${key}`;
            const valueFromSummary = isNil(summary[summaryKey]) ? null : summary[summaryKey];
            result = {
                value: isNil(valueFromSummary) ? null : valueFromSummary
            };
            if (aggregatesWithTimestamps.includes(aggregate)) {
                const timestampFromSummary = summary[`${summaryKey}i`] || undefined;
                result.timestamp = timestampFromSummary;
            }
        }
    }
    return result;
};
export function getMetricLastUpdateFromSummary({ summary, metric, horizonId }) {
    let timestamp;
    const lastUpdate = !isNil(metric)
        ? metric === 'HDEF'
            ? summary.data?.HDEF_Di
            : summary.data?.[`${metric}_Li`]
        : null;
    if (!isNil(lastUpdate)) {
        if (typeof lastUpdate === 'string') {
            timestamp = parseInt(lastUpdate, 10);
        }
        else if (typeof lastUpdate === 'object' && !isNil(horizonId)) {
            const horizonTimestamp = lastUpdate[horizonId];
            if (!isNil(horizonTimestamp)) {
                timestamp = parseInt(horizonTimestamp, 10);
            }
        }
    }
    return timestamp;
}
/**
 * Goes through a plot summary to find non null values
 */
export function hasHydricStressMetricData({ metric, horizonId, summary }) {
    const isCapa = metric === 'U_CAPA';
    const nonNullHorizonId = horizonId === null ? -1 : horizonId;
    const values = hydricStressAggregates.map((key) => {
        if (isCapa) {
            const aggregationKey = `${metric}_${key}`;
            return !isNil(summary.data[aggregationKey]?.[0]) ? summary.data[aggregationKey]?.[0] : null;
        }
        else {
            const aggregationKey = `${metric}_${key}`;
            return !isNil(summary.data[aggregationKey]?.[nonNullHorizonId]?.[0])
                ? summary.data[aggregationKey]?.[nonNullHorizonId]?.[0]
                : null;
        }
    });
    return !isEmpty(values) && values.some((value) => !isNil(value));
}
